var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ReactComponent as BackrsFilledDropShadow } from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { BackrsForm } from '@backrs_ui/common-ui/src/molecules/BackrsForm/BackrsForm';
import { Box, Typography, ButtonBase, useMediaQuery } from '@mui/material';
import { useForm } from 'react-hook-form';
import { AnimatedHeight } from './AnimatedHeight';
import { LoginCarousel } from './LoginCarousel';
import { LoginMethodTabs } from './LoginMethodTabs';
import { OauthButtons } from './OauthButtons';
import { ForgotPasswordPopup } from '../BackrsForm/ForgotPasswordPopup';
var LOGIN_STRINGS = appStrings.modules.login;
var defaultLoginValues = {
    phone: '',
    email: '',
    password: '',
};
export function LoginPage(_a) {
    var title = _a.title, handleLoggingIn = _a.handleLoggingIn, handleSigningUp = _a.handleSigningUp, errorMessage = _a.errorMessage, environmentUrl = _a.environmentUrl, source = _a.source, oauth = _a.oauth;
    var _b = useState(false), isPasswordVisible = _b[0], setIsPasswordVisible = _b[1];
    var _c = useState(false), isForgotPasswordOpen = _c[0], setIsForgotPasswordOpen = _c[1];
    var isMobile = useMediaQuery(function (theme) { return theme.breakpoints.down('md'); });
    var _d = useState(isMobile ? 'phone' : 'email'), loginMethod = _d[0], setLoginMethod = _d[1];
    var methods = useForm({
        defaultValues: defaultLoginValues,
        mode: 'onSubmit',
    });
    var handleMethodChange = function (method) {
        if (method === loginMethod) {
            return;
        }
        setLoginMethod(method);
        methods.clearErrors();
        if (method === 'phone') {
            methods.setValue('email', '');
            methods.setValue('password', '');
        }
        else {
            methods.setValue('phone', '');
        }
    };
    var phoneField = {
        id: 'phone',
        name: 'phone',
        placeholder: LOGIN_STRINGS.phonePlaceholder,
        type: 'tel',
        autoComplete: 'tel',
        autoFocus: true,
        rules: {
            required: LOGIN_STRINGS.error.phoneRequired,
            pattern: {
                value: /^\d{10}$/,
                message: LOGIN_STRINGS.error.invalidPhone,
            },
        },
    };
    var emailField = {
        id: 'email',
        name: 'email',
        placeholder: LOGIN_STRINGS.emailPlaceholder,
        type: 'text',
        autoComplete: 'email',
        autoFocus: true,
        rules: {
            required: LOGIN_STRINGS.error.emailRequired,
            pattern: {
                value: /^\S+@\S+\.\S+$/,
                message: LOGIN_STRINGS.error.invalidEmail,
            },
        },
    };
    var passwordField = {
        id: 'password',
        name: 'password',
        placeholder: LOGIN_STRINGS.passwordPlaceholder,
        type: isPasswordVisible ? 'text' : 'password',
        autoComplete: 'current-password',
        rules: {
            required: LOGIN_STRINGS.error.passwordRequired,
        },
        icon: {
            type: 'RIGHT_ICON',
            rightIcon: isPasswordVisible ? 'EyeOff' : 'Eye',
            onRightIconClick: function () {
                setIsPasswordVisible(function (prev) { return !prev; });
                setTimeout(function () {
                    setIsPasswordVisible(false);
                }, 5000);
            },
        },
    };
    var fields = loginMethod === 'phone' ? [phoneField] : [emailField, passwordField];
    var handleSubmission = function (data) {
        handleLoggingIn(data.phone, data.email, data.password);
    };
    return (_jsxs(Box, { sx: componentContainerSx, children: [_jsx(Box, { sx: containerStyle, children: _jsxs(Box, { sx: loginFormStyle, children: [_jsx(Box, { sx: responsiveBackrsLogoStyle, children: _jsx(BackrsFilledDropShadow, { width: "48px", height: "48px" }) }), _jsxs(Box, { sx: textContainerSx, children: [_jsx(Typography, { variant: "display-xs-semibold", color: function (theme) { return theme.backrs.colors['Colors/Text/text-primary']; }, children: title }), _jsx(Typography, { variant: "text-md-regular", color: function (theme) { return theme.backrs.colors['Colors/Text/text-tertiary']; }, sx: { textAlign: 'center' }, children: LOGIN_STRINGS.welcomeBack })] }), _jsx(LoginMethodTabs, { value: loginMethod, onChange: handleMethodChange }), _jsx(AnimatedHeight, { children: _jsx(BackrsForm, { fields: fields, methods: methods, onSubmit: handleSubmission, buttonLabel: appStrings.buttonText.signIn, buttonId: "login-button", children: _jsx(Box, { sx: forgotPasswordWrapSx(loginMethod === 'email'), children: _jsx(ButtonBase, { id: "forgot-password-button", "data-ph-capture-attribute-name": "forgot-password-button", sx: forgotPasswordContainerStyle, onClick: function () { return setIsForgotPasswordOpen(true); }, disabled: loginMethod !== 'email', disableRipple: true, children: _jsx(Typography, { variant: "text-sm-semibold", sx: forgotPasswordStyle, children: LOGIN_STRINGS.forgotPassword }) }) }) }) }), oauth && (_jsx(Box, { sx: oauthSectionSx, children: _jsx(OauthButtons, __assign({}, oauth)) })), _jsx(ForgotPasswordPopup, { isOpen: isForgotPasswordOpen, setIsOpen: setIsForgotPasswordOpen, environmentUrl: environmentUrl, source: source }), errorMessage && _jsx(Box, { sx: errorMessageSx, children: errorMessage }), handleSigningUp && (_jsxs(Box, { sx: handleSignUpSx, children: [_jsx(Typography, { component: "span", variant: "text-sm-regular", children: LOGIN_STRINGS.dontHaveAccount }), ' ', _jsx(Typography, { id: "signup-button", "data-ph-capture-attribute-name": "signup-button", component: "span", variant: "text-sm-regular", sx: embeddedButtonSx, onClick: handleSigningUp, children: LOGIN_STRINGS.signUp })] }))] }) }), _jsx(Box, { sx: carouselContainerSx, children: _jsx(LoginCarousel, {}) })] }));
}
var forgotPasswordWrapSx = function (isVisible) {
    return function () { return ({
        position: isVisible ? 'static' : 'absolute',
        width: '100%',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        transition: 'opacity 250ms ease',
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
        },
    }); };
};
var errorMessageSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        mt: 2,
        color: backrs.colors['Colors/Text/text-error-primary'],
        '@media (min-width: 900px) and (max-height: 620px)': {
            mt: '-2%',
            mb: '-5%',
        },
    });
};
var handleSignUpSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        paddingBottom: backrs.spacing['spacing-xl'],
    });
};
var embeddedButtonSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'inline',
        color: backrs.colors['Colors/Text/text-brand-tertiary'],
        cursor: 'pointer',
    });
};
var componentContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'grid',
        gridTemplateColumns: {
            xs: '1fr',
            md: '1fr 1fr',
        },
        color: backrs.colors['Yellow/950'],
        height: '100dvh',
        overflow: 'hidden',
    });
};
var carouselContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: {
            xs: 'none',
            md: 'block',
        },
        backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'center',
        width: '100%',
        height: '100dvh',
        borderLeft: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
    });
};
var containerStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        margin: '0 auto',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: {
            xs: 'flex-start',
            md: 'center',
        },
        alignSelf: 'center',
        justifySelf: 'center',
        px: {
            xs: backrs.spacing['spacing-lg'],
            sm: backrs.spacing['spacing-2xl'],
        },
        backgroundColor: backrs.colors['Beige/50'],
        height: '100dvh',
        width: '100%',
        py: {
            xs: backrs.spacing['spacing-6xl'],
            md: backrs.spacing['spacing-4xl'],
        },
    });
};
var loginFormStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        backgroundColor: {
            xs: backrs.colors['Beige/50'],
            sm: backrs.colors['Colors/Base/White'],
        },
        paddingX: {
            xs: backrs.spacing['spacing-md'],
            sm: backrs.spacing['spacing-2xl'],
        },
        borderRadius: {
            xs: 0,
            sm: backrs.radius['radius-md'],
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: {
            xs: '100%',
            sm: '440px',
        },
        width: '100%',
        boxSizing: 'border-box',
        '@media (min-width: 300px)': {
            paddingX: backrs.spacing['spacing-xl'],
        },
        '@media (min-width: 600px)': {
            paddingX: backrs.spacing['spacing-4xl'],
        },
        border: {
            xs: 'none',
            sm: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
        },
        margin: {
            xs: 0,
            sm: "auto ".concat(backrs.spacing['spacing-4xl']),
            md: "auto ".concat(backrs.spacing['spacing-4xl']),
            lg: "auto ".concat(backrs.spacing['spacing-6xl']),
        },
        gap: backrs.spacing['spacing-3xl'],
        pt: backrs.spacing['spacing-2xl'],
        pb: backrs.spacing['spacing-2xl'],
    });
};
var responsiveBackrsLogoStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: '48px',
        maxHeight: '48px',
        mt: backrs.spacing['spacing-3xl'],
    });
};
var forgotPasswordContainerStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'block',
        ml: 'auto',
        mr: backrs.spacing['spacing-md'],
    });
};
var forgotPasswordStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Brand/500'],
    });
};
var textContainerSx = function (theme) { return (__assign(__assign({}, loginFormStyle(theme)), { pt: 0, pb: 0, mt: '4px', gap: '.5rem', outline: 'none', border: 'none', boxShadow: 'none' })); };
var oauthSectionSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-xl'],
    });
};
